import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import {
  Card,
  Container,
  HeaderContainer,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Card';
import { StyledTextFieldContainer, StyledTextField } from '../../../../../components/Inputs';
import { ISConfigOption } from '../../../../../Model/Coach';
import UserContract from '../../../../../Model/UserContract';
import FormsModel, { FormType } from '../../../../Model/Form';
import LeadConfig from '../../../../Model/LeadConfig';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage from '../../../../../hooks/useStorage';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import InputLabel from '../../../../../components/v2/InputLabel';
import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import PrimaryButton from '../../../../../components/Button/ActionButtons/PrimaryButton';
import { isInsideSalesAdmin } from '../../../../../utils/postHog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import ReusableContractModal from '../../../../components/ContractModal/Modals/ReusableContractModal';
import SelectedPlanTerms from '../../../../components/SelectedPlanTerms';
import { getOnboardingLinkFromContract } from '../../../../utils/onboardingLink';
import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import texts from './texts';
import { StyledAutoComplete, StyledContractOption, StyledHelperText } from './styles';

const getContractIdFromLink = (link) => {
  if (!link) return '';
  try {
    return new URL(link).searchParams.get('contractId') || '';
  } catch {
    return '';
  }
};

const renderContractOption = (props, option) => (
  <StyledContractOption {...props} key={option.id}>
    {!!option.label && <span>{option.label}</span>}
    <small>{option.link}</small>
  </StyledContractOption>
);

const DirectPurchaseConfiguration = ({ coachDoc }) => {
  const [pageLoadOfferMessageText, setPageLoadOfferMessageText] = useState('');
  const [postSubmissionOfferMessageText, setPostSubmissionOfferMessageText] = useState('');
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractOptions, setContractOptions] = useState([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [disqualifiedCountriesText, setDisqualifiedCountriesText] = useState('');

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { userDoc } = useContext(LoggedInUserContext);
  const { uploadJSON, getJSONData } = useStorage();

  const isReadOnly = !isInsideSalesAdmin();

  useEffect(() => {
    if (!coachDoc) return;
    setPageLoadOfferMessageText(coachDoc.leadEmailConfig[ISConfigOption.PAGE_LOAD_DIRECT_PURCHASE_OFFER_MESSAGE]
      || texts.pageLoadDisqualificationOfferMessage);
    setPostSubmissionOfferMessageText(
      coachDoc.leadEmailConfig[ISConfigOption.POST_SUBMISSION_DIRECT_PURCHASE_OFFER_MESSAGE]
      || texts.postSubmissionDisqualificationOfferMessage,
    );
    setDisqualifiedCountriesText(coachDoc.disqualifiedCountries.join(', '));
  }, [coachDoc]);

  const fetchContracts = useCallback(async () => {
    if (!coachDoc?.id) return;
    setIsLoadingContracts(true);
    const contractsCollection = await UserContract.getBaseContractsByCoach(coachDoc.id);
    if (isComponentMountedRef.current) {
      const options = contractsCollection.docs
        .map((c) => ({
          id: c.id,
          label: c.name,
          link: getOnboardingLinkFromContract(c.id),
        }));
      const initialContractId = getContractIdFromLink(coachDoc.contractLink);
      setContractOptions(options);
      setSelectedContract(options.find((o) => o.id === initialContractId) || null);
      setIsLoadingContracts(false);
    }
  }, [coachDoc, isComponentMountedRef]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const updateLeadFormStorageJSON = useCallback(async (isConfigFields) => {
    const leadForms = await FormsModel.getFormsByCoachId(coachDoc.id, FormType.LEAD);
    if (!leadForms?.length) return;

    const storagePath = format(storagePaths.COACH_FORM_COLLECTION, {
      [pathPlaceholder.COACH_ID]: coachDoc.id,
    });

    await Promise.all(
      leadForms.map(async (form) => {
        const existingFormData = await getJSONData(`${storagePath}/${form.id}`);
        const updatedFormData = {
          ...existingFormData,
          ...isConfigFields,
        };
        await uploadJSON(JSON.stringify(updatedFormData), storagePath, form.id);
      }),
    );
  }, [coachDoc, getJSONData, uploadJSON]);

  const handleContractChange = useCallback((contract) => {
    setSelectedContract(contract);
  }, []);

  const onSave = useCallback(async () => {
    const directPurchaseLink = selectedContract?.link ?? '';

    if (directPurchaseLink && (!pageLoadOfferMessageText?.trim() || !postSubmissionOfferMessageText?.trim())) {
      showToast(texts.offerMessageRequired, { error: true });
      return;
    }

    const disqualifiedCountries = disqualifiedCountriesText
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    setIsSaving(true);

    try {
      await coachDoc.set({
        leadEmailConfig: {
          ...coachDoc.leadEmailConfig,
          [ISConfigOption.PAGE_LOAD_DIRECT_PURCHASE_OFFER_MESSAGE]: pageLoadOfferMessageText,
          [ISConfigOption.POST_SUBMISSION_DIRECT_PURCHASE_OFFER_MESSAGE]: postSubmissionOfferMessageText,
          [ISConfigOption.CONTRACT_LINK]: directPurchaseLink,
        },
        leadDisqualificationConfig: {
          ...coachDoc.leadDisqualificationConfig,
          countries: {
            ...coachDoc.leadDisqualificationConfig?.countries,
            disallowed: disqualifiedCountries,
          },
        },
        lastUpdatedByUserName: userDoc.name,
      }, { merge: true });

      const leadConfig = new LeadConfig();
      await leadConfig.init();
      const globalAllowedCountries = leadConfig.allowedCountries;
      const allowedCountries = globalAllowedCountries.filter((c) => !disqualifiedCountries.includes(c));

      await updateLeadFormStorageJSON({
        [ISConfigOption.PAGE_LOAD_DIRECT_PURCHASE_OFFER_MESSAGE]: pageLoadOfferMessageText,
        [ISConfigOption.POST_SUBMISSION_DIRECT_PURCHASE_OFFER_MESSAGE]: postSubmissionOfferMessageText,
        [ISConfigOption.CONTRACT_LINK]: directPurchaseLink,
        [ISConfigOption.ALLOWED_COUNTRIES]: allowedCountries,
      });

      showToast(texts.saved, { type: 'success' });
    } catch (error) {
      showToast(texts.saveError, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    pageLoadOfferMessageText,
    postSubmissionOfferMessageText,
    disqualifiedCountriesText,
    selectedContract,
    coachDoc,
    showToast,
    isComponentMountedRef,
    userDoc,
    updateLeadFormStorageJSON,
  ]);

  const handleContractModalClose = useCallback(() => {
    setShowContractModal(false);
    fetchContracts();
  }, [fetchContracts]);

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.pageLoadOfferMessageText}</InputLabel>
          <StyledTextField
            placeholder={texts.pageLoadOfferMessageTextPlaceholder}
            value={pageLoadOfferMessageText}
            onChange={(e) => setPageLoadOfferMessageText(e.target.value)}
            multiline
            rows={3}
            disabled={isReadOnly}
            helperText={texts.pageLoadOfferMessageHelperText}
          />
        </StyledTextFieldContainer>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.postSubmissionOfferMessageText}</InputLabel>
          <StyledTextField
            placeholder={texts.postSubmissionOfferMessageTextPlaceholder}
            value={postSubmissionOfferMessageText}
            onChange={(e) => setPostSubmissionOfferMessageText(e.target.value)}
            multiline
            rows={3}
            disabled={isReadOnly}
            helperText={texts.postSubmissionOfferMessageHelperText}
          />
        </StyledTextFieldContainer>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.directPurchaseLink}</InputLabel>
          {!isLoadingContracts && contractOptions.length === 0 ? (
            <PrimaryButton
              onClick={() => setShowContractModal(true)}
              disabled={isReadOnly}
            >
              {texts.setupDirectPurchaseLink}
            </PrimaryButton>
          ) : (
            <>
              <StyledAutoComplete
                options={contractOptions}
                value={selectedContract}
                onChange={handleContractChange}
                disabled={isReadOnly || isLoadingContracts}
                renderOption={renderContractOption}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.label || option.link}
              />
              <StyledHelperText>{texts.directPurchaseLinkHelperText}</StyledHelperText>
              <SelectedPlanTerms contractLink={selectedContract?.link} />
            </>
          )}
        </StyledTextFieldContainer>
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.disqualifiedCountries}</InputLabel>
          <StyledTextField
            placeholder={texts.disqualifiedCountriesPlaceholder}
            value={disqualifiedCountriesText}
            onChange={(e) => setDisqualifiedCountriesText(e.target.value)}
            disabled={isReadOnly}
            helperText={texts.disqualifiedCountriesNote}
          />
        </StyledTextFieldContainer>
        <SaveButton
          onClick={onSave}
          disabled={isReadOnly}
          $fitToContent
        >
          {texts.save}
        </SaveButton>
      </Container>
      <LoadingOverlay isLoading={!coachDoc || isSaving} />
      <ReusableContractModal
        showModal={showContractModal}
        onClose={handleContractModalClose}
      />
    </Card>
  );
};

DirectPurchaseConfiguration.propTypes = {
  coachDoc: PropTypes.object,
};

DirectPurchaseConfiguration.defaultProps = {
  coachDoc: null,
};

export default DirectPurchaseConfiguration;
